import { useEffect, useMemo, useState } from 'react';
import { FiTool, FiTrash2, FiEdit2 } from 'react-icons/fi';
import { useHubSpotPortals } from '../hooks/useHubSpotPortals';
import { customObjectsApi } from '../services/api';

const EMPTY_FORM = {
  display_name: '',
  hubspot_object_type_id: '',
  object_type_key: '',
  owner_property_name: '',
  is_active: true,
};

export default function ManageCustomObjects() {
  const { selectedPortal } = useHubSpotPortals();
  const portalId = selectedPortal?.portal_id;

  const [customObjects, setCustomObjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const portalLabel = useMemo(() => {
    if (!selectedPortal) return null;
    return `${selectedPortal.hub_domain} (ID: ${selectedPortal.portal_id})`;
  }, [selectedPortal]);

  const loadCustomObjects = async () => {
    if (!portalId) {
      setCustomObjects([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await customObjectsApi.list(portalId);
      setCustomObjects(response.customObjects || []);
    } catch (err) {
      console.error('Failed to load custom objects:', err);
      setError('Failed to load custom objects. Please try again.');
      setCustomObjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSuccess(null);
    loadCustomObjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalId]);

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const onChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!portalId) {
      setError('Select a portal before managing custom objects.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        portal_id: portalId,
        display_name: form.display_name.trim(),
        hubspot_object_type_id: form.hubspot_object_type_id.trim(),
        object_type_key: form.object_type_key.trim() || undefined,
        owner_property_name: form.owner_property_name.trim() || null,
        is_active: !!form.is_active,
      };

      if (editingId) {
        await customObjectsApi.update(editingId, payload);
        setSuccess('Custom object updated.');
      } else {
        await customObjectsApi.create(payload);
        setSuccess('Custom object created.');
      }

      resetForm();
      await loadCustomObjects();
    } catch (err) {
      console.error('Failed to save custom object:', err);
      setError('Failed to save custom object. Please review fields and try again.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      display_name: item.display_name || '',
      hubspot_object_type_id: item.hubspot_object_type_id || '',
      object_type_key: item.object_type_key || '',
      owner_property_name: item.owner_property_name || '',
      is_active: Boolean(item.is_active),
    });
    setError(null);
    setSuccess(null);
  };

  const handleDelete = async (id) => {
    if (!portalId) return;
    if (!window.confirm('Delete this custom object?')) return;

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await customObjectsApi.remove(id, portalId);
      setSuccess('Custom object deleted.');
      if (editingId === id) {
        resetForm();
      }
      await loadCustomObjects();
    } catch (err) {
      console.error('Failed to delete custom object:', err);
      setError('Failed to delete custom object. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center mb-2">
          <FiTool className="w-6 h-6 text-gray-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Manage Custom Objects</h1>
        </div>
        <p className="text-gray-600">Store custom HubSpot object types for portal-specific routing.</p>
        {portalLabel && <p className="text-sm text-blue-700 mt-2">Portal: <span className="font-semibold">{portalLabel}</span></p>}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {editingId ? 'Edit Custom Object' : 'Add Custom Object'}
        </h2>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              value={form.display_name}
              onChange={(e) => onChange('display_name', e.target.value)}
              placeholder="Appointments"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">HubSpot Object Type ID</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              value={form.hubspot_object_type_id}
              onChange={(e) => onChange('hubspot_object_type_id', e.target.value)}
              placeholder="2-12345678"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Internal Object Key (optional)</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              value={form.object_type_key}
              onChange={(e) => onChange('object_type_key', e.target.value)}
              placeholder="custom_appointments"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Owner Property (optional)</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              value={form.owner_property_name}
              onChange={(e) => onChange('owner_property_name', e.target.value)}
              placeholder="hubspot_owner_id"
            />
          </div>

          <div className="md:col-span-2 flex items-center gap-2">
            <input
              id="is_active"
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => onChange('is_active', e.target.checked)}
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">Active</label>
          </div>

          <div className="md:col-span-2 flex justify-end gap-2 mt-2">
            {editingId && (
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                onClick={resetForm}
                disabled={saving}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
              disabled={saving || !portalId}
            >
              {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Custom Object'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Configured Custom Objects</h2>
          {!loading && (
            <span className="text-sm text-gray-500">{customObjects.length} total</span>
          )}
        </div>

        {loading && <p className="text-gray-500">Loading custom objects...</p>}

        {!loading && customObjects.length === 0 && (
          <p className="text-gray-500">No custom objects configured for this portal.</p>
        )}

        {!loading && customObjects.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4">Display Name</th>
                  <th className="py-2 pr-4">HubSpot ID</th>
                  <th className="py-2 pr-4">Key</th>
                  <th className="py-2 pr-4">Active</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customObjects.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-gray-900">{item.display_name}</td>
                    <td className="py-2 pr-4 text-gray-700">{item.hubspot_object_type_id}</td>
                    <td className="py-2 pr-4 text-gray-700">{item.object_type_key}</td>
                    <td className="py-2 pr-4 text-gray-700">{Number(item.is_active) === 1 ? 'Yes' : 'No'}</td>
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                          onClick={() => startEdit(item)}
                          disabled={saving}
                        >
                          <FiEdit2 className="w-4 h-4 mr-1" /> Edit
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center px-2 py-1 border border-red-300 rounded text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(item.id)}
                          disabled={saving}
                        >
                          <FiTrash2 className="w-4 h-4 mr-1" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
          {success}
        </div>
      )}

      {!portalId && (
        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
          Select a HubSpot portal before managing custom objects.
        </div>
      )}

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
        <p className="font-medium mb-1">Format guidance</p>
        <p>Use the HubSpot custom object type ID (example: <code>2-12345678</code>) so routing can map to the correct object.</p>
      </div>
    </div>
  );
}
